/**
 * ChapterList: the chapter rail. Pass chapters as data, or ChapterItems as children.
 * collapsed shows numbers only, with titles as tooltips and accessible names.
 * ChapterItem: one chapter row. Use inside ChapterList.
 * (cockatrice-ui `src/ui/ChapterList.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/chapter-list.css';
import { mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import { component, dynamic, each, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface Chapter {
  /** Stable id. */
  readonly id: string | number;
  /** Chapter number shown in the rail. */
  readonly number: number;
  /** Chapter title. */
  readonly title: string;
  /** Word count. */
  readonly words?: number | undefined;
  /** Link to the chapter (or use hrefFor). */
  readonly href?: string | undefined;
}

export interface ChapterItemProps extends Attributes {
  /** Chapter number. */
  readonly number: number;
  /** Chapter title. */
  readonly title: string;
  /** Word count. */
  readonly words?: number | string | undefined;
  /** Marks the row aria-current="page". */
  readonly current?: boolean | undefined;
  /** Link to the chapter. */
  readonly href?: string | undefined;
  /** A button, not a link: an app whose chapters open without changing its address (`href` is then not used). */
  readonly button?: boolean | undefined;
  /** The button form only: the chapter can't be opened now. */
  readonly disabled?: boolean | undefined;
  /** Numbers only; title becomes tooltip and accessible name. */
  readonly collapsed?: boolean | undefined;
  /** Collapsed only: the accessible name. Default "Chapter {number}: {title}". */
  readonly label?: string | undefined;
  /** Show the drag handle. */
  readonly draggable?: boolean | undefined;
  /** Shows the ⋯ menu button. */
  readonly onMenu?: ((e: MouseEvent) => void) | undefined;
  /** Click on the chapter. */
  readonly onSelect?: ((e: MouseEvent) => void) | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export interface ChapterListProps extends Attributes {
  /** Chapters as data; or pass ChapterItems as children. */
  readonly chapters?: readonly Chapter[] | undefined;
  /** Id of the current chapter. */
  readonly currentId?: Chapter['id'] | undefined;
  /** Numbers only. */
  readonly collapsed?: boolean | undefined;
  /** Show drag handles and "Drag to reorder". */
  readonly draggable?: boolean | undefined;
  /** Name of the nav. Default "Chapters". */
  readonly label?: string | undefined;
  /** Footer text; null hides it. Default "Drag to reorder · N words". */
  readonly footer?: Child;
  /** Total words for the footer; defaults to the sum. */
  readonly totalWords?: number | undefined;
  /** Chapter chosen. */
  readonly onSelect?: ((chapter: Chapter, e: MouseEvent) => void) | undefined;
  /** Shows ⋯ buttons. */
  readonly onMenu?: ((chapter: Chapter, e: MouseEvent) => void) | undefined;
  /** Builds each chapter's link. */
  readonly hrefFor?: ((chapter: Chapter) => string) | undefined;
  /**
   * Collapsed only: each number's accessible name. Default "Chapter {number}: {title}"; an app whose titles may already
   * say "Chapter 3" names them by the title alone (`(chapter) => chapter.title`).
   */
  readonly labelFor?: ((chapter: Chapter) => string) | undefined;
  /** Rows are buttons, not links (no address to go to; `href` and `hrefFor` are not used). */
  readonly button?: boolean | undefined;
  /** The button form only: no chapter can be opened now. */
  readonly disabled?: boolean | undefined;
  /** ChapterItems, when chapters isn't passed. */
  readonly children?: Child;
  /** Extra classes. */
  readonly class?: string | undefined;
}

const extra = (cls: string | undefined): string => (cls !== undefined && cls !== '' ? ` ${cls}` : '');

/** ChapterItem: one chapter row. Use inside ChapterList. */
export function ChapterItem(props: ChapterItemProps): Child {
  const [local, rest] = splitProps(props, ['number', 'title', 'words', 'current', 'href', 'button', 'disabled', 'collapsed', 'label', 'draggable', 'onMenu', 'onSelect', 'class']);
  const words = (): string | undefined => (typeof local.words === 'number' ? local.words.toLocaleString('en-GB') : local.words);
  const collapsed = (): boolean => local.collapsed === true;
  return el(
    'li',
    mergeProps({ class: () => `ui-chapter-item${local.current === true ? ' is-current' : ''}${extra(local.class)}` }, rest),
    when(
      () => local.draggable === true && !collapsed(),
      () => el('span', { class: 'ui-chapter-item__grip', 'aria-hidden': 'true' }, component(Icon, { name: 'grip', size: 'sm' })),
    ),
    dynamic(
      () => (local.button === true ? 'button' : 'a'),
      {
        class: 'ui-chapter-item__link',
        type: () => (local.button === true ? 'button' : undefined),
        href: () => (local.button === true ? undefined : local.href !== undefined && local.href !== '' ? local.href : '#'),
        disabled: () => local.button === true && local.disabled === true,
        'aria-current': () => (local.current === true ? 'page' : undefined),
        'aria-label': () => (collapsed() ? (local.label ?? uiText()('ui.chapter-item.label', { number: String(local.number), title: local.title })) : undefined),
        title: () => (collapsed() ? local.title : undefined),
        onClick: (e: MouseEvent) => {
          local.onSelect?.(e);
        },
      },
      el('span', { class: 'ui-chapter-item__n' }, () => local.number),
      when(
        () => !collapsed(),
        () => [
          el('span', { class: 'ui-chapter-item__title' }, () => local.title),
          when(
            () => local.words !== undefined,
            () => el('span', { class: 'ui-chapter-item__words' }, words),
          ),
        ],
      ),
    ),
    when(
      () => local.onMenu !== undefined && !collapsed(),
      () =>
        el(
          'button',
          {
            type: 'button',
            class: 'ui-chapter-item__menu',
            'aria-label': () => uiText()('ui.chapter-item.more', { number: String(local.number), title: local.title }),
            'aria-haspopup': 'menu',
            onClick: (e: MouseEvent) => {
              local.onMenu?.(e);
            },
          },
          component(Icon, { name: 'more', size: 'sm' }),
        ),
    ),
  );
}

/**
 * ChapterList: the chapter rail. Pass chapters as data, or ChapterItems as children.
 * collapsed shows numbers only, with titles as tooltips and accessible names.
 */
export default function ChapterList(props: ChapterListProps): Child {
  const [local, rest] = splitProps(props, ['chapters', 'currentId', 'collapsed', 'draggable', 'label', 'footer', 'totalWords', 'onSelect', 'onMenu', 'hrefFor', 'labelFor', 'button', 'disabled', 'children', 'class']);
  const footer = (): Child => {
    if (local.footer !== undefined) return local.footer;
    const total = local.totalWords ?? (local.chapters ?? []).reduce((n, c) => n + (typeof c.words === 'number' ? c.words : 0), 0);
    const t = uiText();
    const parts = [local.draggable === true ? t('ui.chapter-list.drag') : null, total ? t('ui.chapter-list.words', { words: total }) : null].filter(Boolean);
    return parts.join(' · ');
  };
  return el(
    'nav',
    mergeProps({
      class: () => `ui-chapter-list${local.collapsed === true ? ' is-collapsed' : ''}${extra(local.class)}`,
      'aria-label': () => (local.label !== undefined && local.label !== '' ? local.label : uiText()('ui.chapter-list.label')),
    }, rest),
    el(
      'ol',
      { class: 'ui-chapter-list__items' },
      when(
        () => local.chapters !== undefined,
        () =>
          each(
            () => local.chapters ?? [],
            (c) =>
              component(ChapterItem, {
                get number() {
                  return c.number;
                },
                get title() {
                  return c.title;
                },
                get words() {
                  return c.words;
                },
                get current() {
                  return c.id === local.currentId;
                },
                get href() {
                  return local.hrefFor ? local.hrefFor(c) : c.href;
                },
                get button() {
                  return local.button;
                },
                get disabled() {
                  return local.disabled;
                },
                get collapsed() {
                  return local.collapsed;
                },
                get label() {
                  return local.labelFor?.(c);
                },
                get draggable() {
                  return local.draggable;
                },
                get onSelect() {
                  const select = local.onSelect;
                  return select
                    ? (e: MouseEvent) => {
                        select(c, e);
                      }
                    : undefined;
                },
                get onMenu() {
                  const menu = local.onMenu;
                  return menu
                    ? (e: MouseEvent) => {
                        menu(c, e);
                      }
                    : undefined;
                },
              }),
          ),
        () => () => local.children,
      ),
    ),
    when(
      () => local.collapsed !== true && Boolean(footer()),
      () => el('p', { class: 'ui-chapter-list__foot' }, footer),
    ),
  );
}
